import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/modules/shared/api/axios";
import { normalizePermissionList } from "@/modules/delegation/config/permissions";

const normalizeUser = (user = {}) => {
  const id = user.id ?? user.user_id ?? user.record_id ?? user.ID ?? user.uuid;
  const rawFirst = user.first_name ?? user.FirstName;
  const rawLast = user.last_name ?? user.LastName;
  const name = user.name ?? user.full_name ?? "";
  const [derivedFirst = "", ...rest] = name.trim().split(" ");
  const derivedLast = rest.join(" ");
  const status = user.status ?? user.Status ?? "active";
  return {
    id,
    first_name: rawFirst ?? derivedFirst,
    last_name: rawLast ?? derivedLast,
    email: user.email ?? user.Email ?? "",
    phone: user.phone ?? user.Phone ?? "",
    role: user.role ?? user.Role ?? "user",
    unified_number: user.unified_number ?? user.unifiedNumber ?? "",
    created_at: user.created_at ?? user.createdAt ?? user.CreatedAt ?? null,
    start_date: user.start_date ?? user.startDate ?? user.Start_Date ?? null,
    status,
    company_name:
      user.company?.name_ar ||
      user.company?.name_en ||
      user.company?.name ||
      user.company_name ||
      "",
    permissions: normalizePermissionList(
      user.user_permissions ??
        user.permissions ??
        user.role_in_company ??
        user.Permissions ??
        ""
    ),
    branch_assignments: (user.branch_assignments || []).map((assignment) => ({
      ...assignment,
      branch_id: Number(assignment.branch_id),
      permissions: normalizePermissionList(assignment.permissions),
      is_default: Boolean(assignment.is_default),
    })),
    default_branch_id: Number(
      user.default_branch_id ||
      user.branch_assignments?.find((assignment) => assignment.is_default)?.branch_id ||
      user.branch_assignments?.[0]?.branch_id ||
      user.branch_id ||
      0
    ) || "",
  };
};

export const fetchAuthorizedUsers = createAsyncThunk(
  "authorizedUsers/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/company/authorized-users");
      const list =
        res.data?.authorized_users ||
        res.data?.users ||
        res.data?.data ||
        [];
      return list.map(normalizeUser);
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const createAuthorizedUser = createAsyncThunk(
  "authorizedUsers/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/company/authorized-users", payload);
      const user = res.data?.user || res.data?.data;
      return normalizeUser(user || payload);
    } catch (err) {
      const data = err.response?.data;
      return rejectWithValue(data || { message: err.message });
    }
  }
);

export const updateAuthorizedUser = createAsyncThunk(
  "authorizedUsers/update",
  async ({ id, data }, { rejectWithValue }) => {
    if (!id) return rejectWithValue({ message: "Missing record id" });
    try {
      const res = await api.put(`/company/authorized-users/${id}`, data);
      const user = res.data?.user || res.data?.data;
      return normalizeUser(user || { id, ...data });
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const deleteAuthorizedUser = createAsyncThunk(
  "authorizedUsers/delete",
  async (id, { rejectWithValue }) => {
    if (!id) return rejectWithValue({ message: "Missing record id" });
    try {
      await api.delete(`/company/authorized-users/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  limitMessage: null,
};

const authorizedUsersSlice = createSlice({
  name: "authorizedUsers",
  initialState,
  reducers: {
    clearAuthorizedErrors(state) {
      state.error = null;
      state.limitMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthorizedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuthorizedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAuthorizedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(createAuthorizedUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.limitMessage = null;
      })
      .addCase(createAuthorizedUser.fulfilled, (state, action) => {
        state.loading = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createAuthorizedUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
        // Wallet shortfalls are handled by the recharge popup, not the plan-limit banner.
        if ((action.payload?.error || action.payload?.message) && action.payload?.code !== "insufficient_wallet_balance") {
          state.limitMessage = action.payload.error || action.payload.message;
        }
      })
      .addCase(updateAuthorizedUser.fulfilled, (state, action) => {
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
      })
      .addCase(updateAuthorizedUser.rejected, (state, action) => {
        state.error = action.payload?.message || action.error.message;
      })
      .addCase(deleteAuthorizedUser.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteAuthorizedUser.rejected, (state, action) => {
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const { clearAuthorizedErrors } = authorizedUsersSlice.actions;

export default authorizedUsersSlice.reducer;
